import {
  BILLING_MODULE_KEYS,
  type BillingModuleKey,
} from '@bill-alps/backend/features/companies/modules';
import type { Plan } from '@bill-alps/backend/shared/billing/planLimits';

/**
 * The Stripe catalogue, as code.
 *
 * One price per *package*: a package is a paid size (`team` / `business`)
 * plus the set of billed modules it covers, and every subscription has exactly
 * one line item. The webhook, the cancellation tombstones and the fiduciaire
 * recompute all reason in `priceId → BillingPackage → { plan, modules }`, so
 * adding a module to the offer only ever adds rows to `BILLING_PACKAGES`.
 *
 * Browser-safe on purpose: the app and the website read the price grid from
 * here (`packagePrice`), so no `process` access outside the env defaults.
 */

type SubscriptionWithItems = {
  items: { data: ReadonlyArray<{ price: { id: string } | null }> };
};

export type PaidPlan = Exclude<Plan, 'free'>;
export const PAID_PLANS: readonly PaidPlan[] = ['team', 'business'];

export type BillingInterval = 'month' | 'year';
export const BILLING_INTERVALS: readonly BillingInterval[] = ['month', 'year'];

/** A set of billed modules — see `normalizeModuleSet` for the canonical form. */
export type BillingModuleSet = readonly BillingModuleKey[];

export const ALL_BILLING_MODULES: BillingModuleSet = BILLING_MODULE_KEYS;

/**
 * Pre-`accounting` stored form of `companies.billedModules`: a single module
 * name, or `'both'` for invoicing + payroll. Read-only from now on; the
 * backfill (`billedModulesV2Backfill`) turns every row into an array.
 */
export type LegacyModuleSelection = 'invoicing' | 'payroll' | 'both';

export function isBillingModuleKey(value: unknown): value is BillingModuleKey {
  return (BILLING_MODULE_KEYS as readonly unknown[]).includes(value);
}

/**
 * Dedupes and orders a module set. Throws on an empty set: a package must
 * bill *something*, and the sites that build one from user input (checkout,
 * trial, plan change) rely on this to refuse the empty selection.
 */
export function normalizeModuleSet(
  modules: Iterable<BillingModuleKey>,
): BillingModuleKey[] {
  const wanted = new Set(modules);
  const ordered = BILLING_MODULE_KEYS.filter((key) => wanted.has(key));
  if (ordered.length === 0) throw new Error('BILLING_MODULES_EMPTY');
  return ordered;
}

/** Set equality — never compare module arrays with `===`. */
export function sameModuleSet(
  a: BillingModuleSet,
  b: BillingModuleSet,
): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((key) => bSet.has(key));
}

export type ModuleCombo =
  | 'invoicing'
  | 'payroll'
  | 'accounting'
  | 'invoicing_payroll'
  | 'invoicing_accounting'
  | 'payroll_accounting'
  | 'invoicing_payroll_accounting';

/** Every non-empty subset of the billed modules, in canonical order. */
export const MODULE_COMBOS: readonly ModuleCombo[] = [
  'invoicing',
  'payroll',
  'accounting',
  'invoicing_payroll',
  'invoicing_accounting',
  'payroll_accounting',
  'invoicing_payroll_accounting',
];

export function moduleCombo(modules: Iterable<BillingModuleKey>): ModuleCombo {
  return normalizeModuleSet(modules).join('_') as ModuleCombo;
}

export function modulesOfCombo(combo: ModuleCombo): BillingModuleKey[] {
  return combo.split('_') as BillingModuleKey[];
}

export type BillingPackage = `${PaidPlan}_${ModuleCombo}`;

export interface BillingPackageConfig {
  plan: PaidPlan;
  combo: ModuleCombo;
  modules: BillingModuleKey[];
}

export const BILLING_PACKAGES: Record<BillingPackage, BillingPackageConfig> =
  Object.fromEntries(
    PAID_PLANS.flatMap((plan) =>
      MODULE_COMBOS.map((combo) => [
        `${plan}_${combo}`,
        { plan, combo, modules: modulesOfCombo(combo) },
      ]),
    ),
  ) as Record<BillingPackage, BillingPackageConfig>;

export const BILLING_PACKAGE_KEYS = Object.keys(
  BILLING_PACKAGES,
) as BillingPackage[];

export function isBillingPackage(value: unknown): value is BillingPackage {
  return typeof value === 'string' && value in BILLING_PACKAGES;
}

export function getBillingPackage(
  plan: PaidPlan,
  modules: Iterable<BillingModuleKey>,
): BillingPackage {
  return `${plan}_${moduleCombo(modules)}`;
}

// ---- Prices -----------------------------------------------------------------

/**
 * The whole grid, CHF per month excl. VAT, by number of billed modules. One
 * rule: a module is 15 (Équipe) or 29 (Business), degressive, capped by the
 * three-module Suite. The 1- and 2-module cells are the six pre-existing
 * Stripe prices, so no current subscriber moves.
 *
 * Single source of truth: `setupStripe.ts`, the in-app plan picker and the
 * website (`apps/website/src/lib/pricing.ts` re-exports it) all derive from
 * here.
 */
export const PACKAGE_PRICES_CHF: Record<PaidPlan, Record<1 | 2 | 3, number>> = {
  team: { 1: 15, 2: 24, 3: 29 },
  business: { 1: 29, 2: 49, 3: 59 },
};

/** Yearly billing includes two free months. */
export const YEARLY_MONTHS = 10;

/**
 * Swiss standard VAT, applied on top of every package price. The whole grid
 * is quoted excl. VAT (see `PACKAGE_PRICES_CHF` and docs/PRICING.md), so the
 * Stripe prices carry `tax_behavior: 'exclusive'` and the rate is added by the
 * `STRIPE_TAX_RATE_ID` tax rate rather than baked into the amounts.
 */
export const SWISS_VAT_PERCENT = 8.1;

/** A price excl. VAT turned into what the customer actually pays. */
export function priceWithVat(amountExclVat: number): number {
  return amountExclVat * (1 + SWISS_VAT_PERCENT / 100);
}

export type ModuleCount = 1 | 2 | 3;

export function moduleCountOf(
  modules: Iterable<BillingModuleKey>,
): ModuleCount {
  return normalizeModuleSet(modules).length as ModuleCount;
}

/** Price of a package for one billing period (a month or a year), in CHF. */
export function packagePrice(
  plan: Plan,
  moduleCount: ModuleCount,
  interval: BillingInterval = 'month',
): number {
  if (plan === 'free') return 0;
  const monthly = PACKAGE_PRICES_CHF[plan][moduleCount];
  return interval === 'year' ? monthly * YEARLY_MONTHS : monthly;
}

/** Same, addressed by module set rather than count. */
export function packagePriceFor(
  plan: Plan,
  modules: Iterable<BillingModuleKey>,
  interval: BillingInterval = 'month',
): number {
  return packagePrice(plan, moduleCountOf(modules), interval);
}

// ---- Env / price ids --------------------------------------------------------

export type StripePriceEnv = Readonly<Record<string, string | undefined>>;

function defaultPriceEnv(): StripePriceEnv {
  return typeof process === 'undefined' ? {} : process.env;
}

/**
 * `STRIPE_PRICE_<TIER>_<COMBO>_<INTERVAL>`, e.g.
 * `STRIPE_PRICE_TEAM_INVOICING_PAYROLL_MONTH`. Templated so the 28 names
 * never have to be listed by hand.
 */
export function priceEnvName(
  billingPackage: BillingPackage,
  interval: BillingInterval,
): string {
  const { plan, combo } = BILLING_PACKAGES[billingPackage];
  return `STRIPE_PRICE_${plan}_${combo}_${interval}`.toUpperCase();
}

export interface PriceCatalogEntry {
  env: string;
  package: BillingPackage;
  interval: BillingInterval;
  /** One of the six prices that predate the accounting module. */
  legacy: boolean;
}

/**
 * The six env vars of the original catalogue, mapped to their *strict*
 * equivalent: a bundle subscriber paid for invoicing + payroll, not for
 * accounting (no grandfathering — see docs/PRICING.md). Kept in the catalogue
 * for good: the prices stay active in Stripe and existing subscribers keep
 * them.
 */
export const LEGACY_PRICE_ENTRIES: readonly PriceCatalogEntry[] = [
  {
    env: 'STRIPE_TEAM_INVOICING_PRICE_ID',
    package: 'team_invoicing',
    interval: 'month',
    legacy: true,
  },
  {
    env: 'STRIPE_TEAM_PAYROLL_PRICE_ID',
    package: 'team_payroll',
    interval: 'month',
    legacy: true,
  },
  {
    env: 'STRIPE_TEAM_BUNDLE_PRICE_ID',
    package: 'team_invoicing_payroll',
    interval: 'month',
    legacy: true,
  },
  {
    env: 'STRIPE_BUSINESS_INVOICING_PRICE_ID',
    package: 'business_invoicing',
    interval: 'month',
    legacy: true,
  },
  {
    env: 'STRIPE_BUSINESS_PAYROLL_PRICE_ID',
    package: 'business_payroll',
    interval: 'month',
    legacy: true,
  },
  {
    env: 'STRIPE_BUSINESS_BUNDLE_PRICE_ID',
    package: 'business_invoicing_payroll',
    interval: 'month',
    legacy: true,
  },
];

/** The 28 current entries followed by the 6 legacy ones. */
export const PRICE_CATALOG: readonly PriceCatalogEntry[] = [
  ...BILLING_PACKAGE_KEYS.flatMap((billingPackage) =>
    BILLING_INTERVALS.map((interval) => ({
      env: priceEnvName(billingPackage, interval),
      package: billingPackage,
      interval,
      legacy: false,
    })),
  ),
  ...LEGACY_PRICE_ENTRIES,
];

export const TAX_RATE_ENV = 'STRIPE_TAX_RATE_ID';

/**
 * `default_tax_rates` for a new subscription: the single Swiss VAT rate, or
 * nothing at all when the deployment has no rate configured.
 *
 * Deliberately NOT fail-closed, unlike `getPriceIdForPackage`: a missing rate
 * must not stop a customer from subscribing. It under-bills VAT instead, which
 * is why `TAX_RATE_ENV` is part of the boot-time warning below.
 */
export function getTaxRateIds(
  env: StripePriceEnv = defaultPriceEnv(),
): string[] {
  const id = env[TAX_RATE_ENV];
  return id ? [id] : [];
}

/**
 * One dedicated portal configuration PER TARGET SIZE: Stripe caps the portal
 * catalogue at 10 product entries with one price per billing interval each,
 * so a single configuration cannot list the whole 7 × 4 grid — see
 * `portalCatalog.ts` for the verified constraints.
 */
export const PORTAL_CONFIGURATION_ENVS: Record<PaidPlan, string> = {
  team: 'STRIPE_PORTAL_CONFIGURATION_ID_TEAM',
  business: 'STRIPE_PORTAL_CONFIGURATION_ID_BUSINESS',
};

/**
 * The dedicated portal configuration used by the plan-change deep link
 * (`createSubscriptionUpdateFlow`) for a given TARGET size, created by
 * `scripts/billing/setupPortalConfiguration.ts`.
 *
 * Deliberately fail-open, like `getTaxRateIds`: when the id is missing the
 * caller omits `configuration` and Stripe falls back to the account's default
 * portal configuration — the pre-rollout behaviour. Failing closed here would
 * turn a missing env var into a broken plan-change button, which is why the
 * names are part of the boot-time warning below instead.
 */
export function getPortalConfigurationId(
  plan: PaidPlan,
  env: StripePriceEnv = defaultPriceEnv(),
): string | undefined {
  return env[PORTAL_CONFIGURATION_ENVS[plan]] || undefined;
}

/**
 * Env names with no id configured — logged at boot, see http.ts. Covers the
 * price catalogue plus the VAT rate and the portal configurations.
 */
export function missingPriceEnvNames(
  env: StripePriceEnv = defaultPriceEnv(),
): string[] {
  const missing = PRICE_CATALOG.filter((entry) => !env[entry.env]).map(
    (entry) => entry.env,
  );
  if (!env[TAX_RATE_ENV]) missing.push(TAX_RATE_ENV);
  for (const plan of PAID_PLANS) {
    const name = PORTAL_CONFIGURATION_ENVS[plan];
    if (!env[name]) missing.push(name);
  }
  for (const module of BILLING_MODULE_KEYS) {
    const name = fiduciairePriceEnvName(module);
    if (!env[name]) missing.push(name);
  }
  return missing;
}

/**
 * The price to put on a new subscription. Prefers the current catalogue and
 * falls back to the legacy monthly price of the same package, so a deployment
 * that has not run `setupStripe.ts` yet keeps selling its six old packages.
 */
export function getPriceIdForPackage(
  billingPackage: BillingPackage,
  interval: BillingInterval = 'month',
  env: StripePriceEnv = defaultPriceEnv(),
): string {
  const envName = priceEnvName(billingPackage, interval);
  const current = env[envName];
  if (current) return current;
  const legacy = LEGACY_PRICE_ENTRIES.find(
    (entry) => entry.package === billingPackage && entry.interval === interval,
  );
  const fallback = legacy ? env[legacy.env] : undefined;
  if (fallback) return fallback;
  throw new Error(`${envName} environment variable not set`);
}

export interface ResolvedPrice {
  package: BillingPackage;
  interval: BillingInterval;
  legacy: boolean;
}

/**
 * Fail-closed lookup of a Stripe price id. An unknown id throws, which the
 * webhook turns into a 500 + retry — never into a silently unbilled company.
 */
export function resolvePriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = defaultPriceEnv(),
): ResolvedPrice {
  if (!priceId) {
    throw new Error('Stripe price ID is missing');
  }
  for (const entry of PRICE_CATALOG) {
    const configured = env[entry.env];
    if (configured && configured === priceId) {
      return {
        package: entry.package,
        interval: entry.interval,
        legacy: entry.legacy,
      };
    }
  }
  throw new Error(`Unknown Stripe price ID: ${priceId}`);
}

export function getPlanFromPriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = defaultPriceEnv(),
): PaidPlan {
  return BILLING_PACKAGES[resolvePriceId(priceId, env).package].plan;
}

/** Package + interval of a subscription — always its first (only) item. */
export function getBillingFromSubscription(
  subscription: SubscriptionWithItems,
  env: StripePriceEnv = defaultPriceEnv(),
): ResolvedPrice {
  return resolvePriceId(subscription.items.data[0]?.price?.id, env);
}

export function getPlanFromSubscription(
  subscription: SubscriptionWithItems,
  env: StripePriceEnv = defaultPriceEnv(),
): PaidPlan {
  return BILLING_PACKAGES[getBillingFromSubscription(subscription, env).package]
    .plan;
}

// ---- Fiduciaire per-module prices -------------------------------------------

/**
 * One volume-tiered price PER MODULE (quantity = number of fiduciaire-paid
 * mandates carrying that module). Separate from the package prices on
 * purpose: fiduciaire billing is user-level, not per-company. Created by
 * `setupStripe.ts --fiduciaire`.
 */
export function fiduciairePriceEnvName(module: BillingModuleKey): string {
  return `STRIPE_FIDUCIAIRE_PRICE_${module.toUpperCase()}`;
}

export const FIDUCIAIRE_PRICE_ENVS: readonly string[] = BILLING_MODULE_KEYS.map(
  (module) => fiduciairePriceEnvName(module),
);

/** Fail-closed: a missing env var must never checkout the wrong price. */
export function getFiduciairePriceId(
  module: BillingModuleKey,
  env: StripePriceEnv = defaultPriceEnv(),
): string {
  const name = fiduciairePriceEnvName(module);
  const priceId = env[name];
  if (!priceId) {
    throw new Error(`${name} environment variable not set`);
  }
  return priceId;
}

/**
 * Fail-closed reverse lookup: which module a fiduciaire subscription item
 * bills. An unknown id throws — webhook retries beat silent mis-billing.
 */
export function resolveFiduciairePriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = defaultPriceEnv(),
): BillingModuleKey {
  if (!priceId) {
    throw new Error('Stripe price ID is missing');
  }
  for (const module of BILLING_MODULE_KEYS) {
    const configured = env[fiduciairePriceEnvName(module)];
    if (configured && configured === priceId) return module;
  }
  throw new Error(`Unknown fiduciaire Stripe price ID: ${priceId}`);
}
